import {
    CustomizationTheme,
    type CustomizationThemedColor,
    CustomizationThemeMode,
    type SiteCustomizationSettings,
} from '@gitbook/api-client';
import type { ContentThemeMode } from '@gitbook/app-database';
import {
    colorContrast,
    hexToRgbArray,
    oklchToRgb,
    rgbArrayToHex,
    rgbToOklch,
} from '@gitbook/colors';
import { colors } from '@gitbook/react-uikit';
import { assertNever } from '@gitbook/util-ts';

/**
 * Generate tint suggestions for a given primary color, grouped by subtlety.
 */
export function generateTintSuggestions(primaryColor: CustomizationThemedColor) {
    const lightOklch = rgbToOklch(hexToRgbArray(primaryColor.light));
    const darkOklch = rgbToOklch(hexToRgbArray(primaryColor.dark));

    const subtleColors = [
        // Nearly neutral, barely tinted
        createThemedColor(lightOklch.H, 0.02, 0.9, 0.1),
        // Very subtle tint
        createThemedColor(lightOklch.H, 0.05, 0.85, 0.15),
        // Light subtle tint
        createThemedColor(lightOklch.H, 0.08, 0.8, 0.2),
    ];

    const baseChroma = Math.min(lightOklch.C, darkOklch.C);
    const boldColors = [
        // Medium saturation
        createThemedColor(lightOklch.H, baseChroma * 0.8, 0.7, 0.3),
        // Medium-high saturation
        createThemedColor(lightOklch.H, baseChroma, 0.6, 0.25),
        // Highly saturated
        createThemedColor(lightOklch.H, baseChroma * 1.2, 0.5, 0.2),
    ];

    return [
        {
            title: 'Subtle',
            colors: subtleColors,
        },
        {
            title: 'Bold',
            colors: boldColors,
        },
    ];
}

/**
 * Create a themed color based on the given hue, chroma, and lightness.
 */
function createThemedColor(
    hue: number,
    chroma: number,
    lightLight: number,
    lightDark: number
): CustomizationThemedColor {
    const light = oklchToRgb({
        L: lightLight,
        C: chroma,
        H: hue,
    });

    const dark = oklchToRgb({
        L: lightDark,
        C: chroma,
        H: hue,
    });

    return {
        light: rgbArrayToHex(light).toUpperCase(),
        dark: rgbArrayToHex(dark).toUpperCase(),
    };
}

export function getHeaderPreviewBackground(
    settings: SiteCustomizationSettings,
    themeMode: ContentThemeMode
): string {
    const lightBackground = colors.WHITE;
    const darkBackground = colors.darkModeGreyScale.xdark;
    const defaultBackground =
        themeMode === CustomizationThemeMode.Light ? lightBackground : darkBackground;

    switch (settings.styling.theme) {
        case CustomizationTheme.Clean:
            return defaultBackground;

        case CustomizationTheme.Muted:
            return themeMode === CustomizationThemeMode.Light
                ? colors.lightModeGreyScale.xxlight
                : colors.darkModeGreyScale.base;

        case CustomizationTheme.Bold:
            return (
                settings.styling.tint?.color?.[themeMode] ||
                settings.styling.primaryColor?.[themeMode] ||
                settings.styling.primaryColor?.light ||
                defaultBackground
            );

        case CustomizationTheme.Gradient: {
            const gradientColor =
                settings.styling.tint?.color?.[themeMode] ??
                settings.styling.primaryColor?.[themeMode] ??
                defaultBackground;
            return `linear-gradient(to bottom left, color-mix(in srgb, ${gradientColor} 50%, ${defaultBackground}), ${defaultBackground})`;
        }
        default:
            assertNever(settings.styling.theme);
    }
}

export function getHeaderPreviewForeground(
    settings: SiteCustomizationSettings,
    themeMode: ContentThemeMode
): string {
    const lightForeground = colors.lightModeGreyScale.xdark;
    const darkForeground = colors.WHITE;
    const defaultForeground =
        themeMode === CustomizationThemeMode.Light ? lightForeground : darkForeground;

    switch (settings.styling.theme) {
        case CustomizationTheme.Clean:
            return defaultForeground;

        case CustomizationTheme.Muted:
            return defaultForeground;

        case CustomizationTheme.Bold:
            return colorContrast(
                settings.styling.tint?.color?.[themeMode] ||
                    settings.styling.primaryColor?.[themeMode] ||
                    settings.styling.primaryColor?.light ||
                    defaultForeground
            );

        case CustomizationTheme.Gradient:
            return defaultForeground;

        default:
            assertNever(settings.styling.theme);
    }
}
